export interface OrganizationRouteDescriptor {
  id: string;
  slug: string;
  isPrimary: boolean;
  vaults: Array<{ slug: string; href: string }>;
}

/**
 * Routes that should always use the platform (primary) organization context.
 * These are generic/utility pages not belonging to any specific organization.
 * Note: 'login-required' and 'no-access' are intentionally NOT in this list
 * because they can exist under org routes (/{org}/login-required, /{org}/no-access)
 * to preserve menu context.
 */
const PLATFORM_ORG_ROUTES = [
  'welcome',
  'v', // /v/{vault} routes use platform org
  'auth',
  'contact',
  'search',
  'search-results',
  'showcase',
  'vaults',
  'admin',
  'register',
];

export function resolveOrganizationFromPath(
  pathname: string,
  organizations: OrganizationRouteDescriptor[],
  fallback?: OrganizationRouteDescriptor | null,
): OrganizationRouteDescriptor | null {
  if (!organizations.length) {
    return null;
  }

  const sanitizedPath = sanitizePath(pathname);
  const segments = sanitizedPath
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (!segments.length) {
    return fallback ?? organizations[0] ?? null;
  }

  const candidateSlug = segments[0];

  // Generic/utility routes always use platform org (fallback)
  if (PLATFORM_ORG_ROUTES.includes(candidateSlug)) {
    return fallback ?? organizations[0] ?? null;
  }

  const match = organizations.find((org) => org.slug === candidateSlug);
  if (match) {
    return match;
  }

  return fallback ?? organizations[0] ?? null;
}

export function getOrganizationDefaultHref(organization: OrganizationRouteDescriptor): string | null {
  const firstVault = organization.vaults.find((vault) => Boolean(vault.href));
  if (firstVault) {
    return firstVault.href;
  }

  if (organization.isPrimary) {
    return '/vaults/create';
  }

  if (!organization.slug) {
    return '/vaults/create';
  }

  return `/${organization.slug}/vaults/create`;
}

function sanitizePath(pathname: string): string {
  if (!pathname || pathname.length === 0) {
    return '/';
  }
  if (!pathname.startsWith('/')) {
    return `/${pathname}`;
  }
  return pathname;
}
